import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import { useCities } from '@/api/addresses/queries/useCities'
import type { Address } from '@/api/addresses/types/Address'
import { PkgPricesResponse } from '@/api/payments/types/PkgPricesResponse'
import { useUpdateUserPkgShippingMethod } from '@/api/user-package/mutations/useUpdateUserPkgShippingMethod'
import { PkgShippingMethod, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppDialog } from '@/components/AppDialog'
import { useConfirm } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { RoutePath } from '@/RoutePath'

import EditAddressDialog from '../Profile/cmps/EditAddressDialog'
import { AddressSelector } from './components/AddressSelector'
import { OptionCard } from './components/OptionCard'
import { ShippingMethodSelector } from './components/ShippingMethodSelector'
export interface ShippingOptions {
  selectedAddressId: string
  selectedShippingMethod: PkgShippingMethod
  isInsuranceAdded: boolean
}

interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  onUpdateShippingOptions: (options: Partial<ShippingOptions>) => void
  shippingPrices: PkgPricesResponse['shippingPrices']
  insurancePrice: number
}

export default function ShippingPage({ pkg, shippingOptions, onUpdateShippingOptions, shippingPrices, insurancePrice }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pkgId } = useParams<{ pkgId: string }>()
  const [showInsuranceModal, setShowInsuranceModal] = useState(false)
  const { data: addresses, isLoading: isLoadingAddresses } = useAddresses()
  const { confirm, ConfirmDialog } = useConfirm()
  const { data: cities = [], isLoading: isCitiesLoading } = useCities()
  const { showWarningToast } = useToast()
  const [showAddAddress, setShowAddAddress] = useState(false)
  const [addressPendingStreetFix, setAddressPendingStreetFix] = useState<Address | null>(null)
  const { mutateAsync: updateShippingMethod, isPending: isSavingShippingMethod } = useUpdateUserPkgShippingMethod()

  const openAddAddressDialog = () => {
    setAddressPendingStreetFix(null)
    setShowAddAddress(true)
  }

  const handleContinue = async () => {
    const selectedAddress = addresses?.find(addr => addr.id === shippingOptions.selectedAddressId)
    if (!shippingOptions.selectedAddressId || !selectedAddress) {
      openAddAddressDialog()
      return
    }
    const placeCity = cities.find(c => c.city.trim() === selectedAddress.city.trim())
    if (placeCity?.hasStreets && (!selectedAddress.street?.trim() || !selectedAddress.house?.trim())) {
      setShowAddAddress(false)
      setAddressPendingStreetFix(selectedAddress)
      showWarningToast(t('payment.missingStreetTitle'), t('payment.missingStreetDescription'), { disableAutoClose: true })
      return
    }
    let confirmed = true
    if (!shippingOptions.isInsuranceAdded) {
      confirmed = await confirm({
        title: t('payment.continueWithoutInsuranceTitle'),
        description: t('payment.continueWithoutInsuranceContent'),
        confirmLabel: t('payment.continueWithoutInsurance'),
        cancelLabel: t('common.back'),
      })
    }
    if (confirmed) {
      await updateShippingMethod({ pkgId: pkg.id, shippingMethod: shippingOptions.selectedShippingMethod })
      // Navigate to payment summary page
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }

  const handleAddressCreated = (createdAddress?: { id: string }) => {
    if (createdAddress) {
      onUpdateShippingOptions({ selectedAddressId: createdAddress.id })
    }
  }

  const handleAddressDialogSuccess = (created?: Address) => {
    if (created) {
      handleAddressCreated(created)
    }
    setAddressPendingStreetFix(null)
  }

  const handleAddressDialogOpenChange = (open: boolean) => {
    if (!open) {
      setShowAddAddress(false)
      setAddressPendingStreetFix(null)
    }
  }

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>{t('payment.selectShippingTitle')}</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>{t('payment.selectShippingDesc')}</p>
      </div>

      {/* Address Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.selectAddress')}</h3>
        <AddressSelector
          onAddAddress={openAddAddressDialog}
          selectedAddressId={shippingOptions.selectedAddressId}
          onSelectAddress={addressId => onUpdateShippingOptions({ selectedAddressId: addressId })}
        />
      </div>

      {/* Shipping Method Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.selectShippingType')}</h3>
        <ShippingMethodSelector
          selectedMethod={shippingOptions.selectedShippingMethod}
          onSelectMethod={method => onUpdateShippingOptions({ selectedShippingMethod: method })}
          packageValue={pkg.valueInUSD || 0}
          shippingPrices={shippingPrices}
        />
      </div>

      {/* Additional Options */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.additionalOptions')}</h3>
        <OptionCard
          onSelect={() => onUpdateShippingOptions({ isInsuranceAdded: !shippingOptions.isInsuranceAdded })}
          selected={shippingOptions.isInsuranceAdded}
          title={t('payment.addInsuranceTitle')}
          description={
            <p>
              {t('payment.insuranceOptionDescription')}{' '}
              <Button variant='link' className='text-sm font-normal' size='sm' onClick={() => setShowInsuranceModal(true)}>
                {t('payment.addInsuranceMoreInfo')}
              </Button>
            </p>
          }
          endContent={insurancePrice > 0 ? <p>${insurancePrice.toFixed(2)}</p> : null}
        />
      </div>

      {/* Continue Button */}
      <div className='space-y-4'>
        <Button
          onClick={handleContinue}
          loading={isSavingShippingMethod}
          disabled={isLoadingAddresses || isCitiesLoading || isSavingShippingMethod}
          className='w-full'
          size='lg'
        >
          {!shippingOptions.selectedAddressId && !addresses?.length ? t('payment.addAddressOrContinue') : t('payment.continueToSummary')}
        </Button>
      </div>

      {/* Insurance Info Modal */}
      <AppDialog isOpen={showInsuranceModal} title={t('payment.insuranceModalTitle')} onOpenChange={setShowInsuranceModal}>
        <div className='text-text-secondary space-y-4'>
          <p>{t('payment.insuranceModalIntro')}</p>
          <div>
            <p className='font-medium'>{t('payment.insuranceAllows')}</p>
            <ul className='mt-2 space-y-1 text-sm'>
              <li>• {t('payment.insuranceBullet1')}</li>
              <li>• {t('payment.insuranceBullet2')}</li>
              <li>• {t('payment.insuranceBullet3')}</li>
            </ul>
          </div>
          <div>
            <p className='font-medium'>{t('payment.insuranceAttention')}</p>
            <p className='text-sm'>{t('payment.insuranceFragileNote')}</p>
          </div>
          <div className='flex flex-col items-center gap-2 pt-6'>
            <p className='text-xs'>
              *{t('payment.insuranceTerms')}
              <br />
              {t('payment.insuranceKnowMore')}{' '}
              <a
                href='https://www.shiptanbul.com/knowledge-center/faqs/insurance'
                target='_blank'
                rel='noreferrer'
                className='text-text-link hover:underline'
              >
                {t('payment.insuranceClickHere')}
              </a>
            </p>
            <Button onClick={() => setShowInsuranceModal(false)} variant='outline'>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </AppDialog>
      <EditAddressDialog
        isOpen={showAddAddress || addressPendingStreetFix !== null}
        onOpenChange={handleAddressDialogOpenChange}
        editingAddress={addressPendingStreetFix}
        onSuccess={handleAddressDialogSuccess}
      />
      {ConfirmDialog}
    </div>
  )
}
